// ResetPassword.js — Страница создания нового пароля
// Открывается по ссылке из email сброса пароля (/app/?token=xxx#/reset-password)
import React, { useState } from 'react';
import { resetPasswordWithToken } from './api';
import { S } from './strings';
import { stripCyrillic } from './shared';

const STYLES = {
  page: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: '#f0f2f5',
    fontFamily: "Arial, 'Helvetica Neue', Helvetica, sans-serif",
    padding: '16px',
  },
  card: {
    width: '100%',
    maxWidth: 420,
    background: '#ffffff',
    borderRadius: 12,
    boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
    overflow: 'hidden',
  },
  header: {
    background: '#1e3a5f',
    padding: '32px 24px',
    textAlign: 'center',
  },
  logo: {
    fontSize: 28,
    fontWeight: 800,
    color: '#ffffff',
    letterSpacing: 3,
  },
  logoSub: {
    fontSize: 12,
    color: 'rgba(255,255,255,0.65)',
    marginTop: 4,
  },
  body: {
    padding: '36px 28px 32px',
  },
  title: {
    fontSize: 20,
    fontWeight: 700,
    color: '#1e3a5f',
    marginBottom: 8,
    textAlign: 'center',
  },
  subtitle: {
    fontSize: 14,
    color: '#718096',
    textAlign: 'center',
    marginBottom: 28,
    lineHeight: 1.5,
  },
  label: {
    display: 'block',
    fontSize: 13,
    fontWeight: 600,
    color: '#4a5568',
    marginBottom: 6,
  },
  input: {
    width: '100%',
    padding: '12px 14px',
    fontSize: 15,
    border: '1.5px solid #d0dae8',
    borderRadius: 8,
    outline: 'none',
    background: '#f7f9fc',
    color: '#2d3748',
    marginBottom: 18,
    boxSizing: 'border-box',
    transition: 'border-color 0.2s',
  },
  inputFocus: {
    borderColor: '#2b6cb0',
  },
  btn: {
    width: '100%',
    padding: '14px',
    fontSize: 16,
    fontWeight: 700,
    color: '#ffffff',
    background: '#2b6cb0',
    border: 'none',
    borderRadius: 8,
    cursor: 'pointer',
    letterSpacing: 0.3,
    transition: 'background 0.2s',
  },
  btnDisabled: {
    background: '#a0aec0',
    cursor: 'not-allowed',
  },
  error: {
    background: '#fff5f5',
    border: '1px solid #feb2b2',
    borderRadius: 8,
    padding: '10px 14px',
    fontSize: 13,
    color: '#c53030',
    marginBottom: 18,
    textAlign: 'center',
  },
  success: {
    background: '#edf7ed',
    border: '1px solid #9ae6b4',
    borderRadius: 8,
    padding: '10px 14px',
    fontSize: 13,
    color: '#276749',
    marginBottom: 18,
    textAlign: 'center',
  },
  hint: {
    fontSize: 12,
    color: '#a0aec0',
    marginTop: -12,
    marginBottom: 18,
  },
};

// Извлечение токена из URL (?token=xxx)
function getTokenFromURL() {
  const params = new URLSearchParams(window.location.search);
  return params.get('token') || '';
}

export default function ResetPassword() {
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [focusedField, setFocusedField] = useState(null);

  const t = (S().resetPwd || {});
  const token = getTokenFromURL();

  const passwordValid = password.length >= 8;
  const passwordsMatch = password === confirm;
  const canSubmit = passwordValid && passwordsMatch && !loading && !success;

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;

    if (!token) {
      setError(t.noToken || 'Токен сброса пароля не найден. Запросите новую ссылку.');
      return;
    }

    setError('');
    setLoading(true);

    try {
      await resetPasswordWithToken(token, password);
      setSuccess(true);
      // Убираем токен из URL, чтобы F5 не ломал страницу
      setTimeout(() => {
        window.location.replace('/app/');
      }, 2500);
    } catch (err) {
      setError(err.message || (t.failed || 'Не удалось сменить пароль. Попробуйте снова.'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={STYLES.page}>
      <div style={STYLES.card}>
        {/* Header */}
        <div style={STYLES.header}>
          <div style={STYLES.logo}>INMONEX</div>
          <div style={STYLES.logoSub}>{t.logoSub || 'Контроль денег. Свобода решений'}</div>
        </div>

        {/* Body */}
        <div style={STYLES.body}>
          <div style={STYLES.title}>{t.title || 'Создание нового пароля'}</div>
          <div style={STYLES.subtitle}>
            {t.subtitle || 'Придумайте надёжный пароль для вашего аккаунта'}
          </div>

          {error && <div style={STYLES.error}>{error}</div>}

          {success ? (
            <div style={STYLES.success}>
              {t.successMsg || 'Пароль успешно изменён! Сейчас откроется страница входа...'}
            </div>
          ) : (
            <form onSubmit={onSubmit}>
              <label style={STYLES.label}>{t.newPwd || 'Новый пароль'}</label>
              <input
                type="password"
                value={password}
                onChange={e => setPassword(stripCyrillic(e.target.value))}
                onFocus={() => setFocusedField('password')}
                onBlur={() => setFocusedField(null)}
                placeholder={t.newPwdPh || 'Минимум 8 символов'}
                style={{
                  ...STYLES.input,
                  ...(focusedField === 'password' ? STYLES.inputFocus : {}),
                }}
                autoComplete="new-password"
              />
              {password.length > 0 && !passwordValid && (
                <div style={{ ...STYLES.hint, color: '#e53e3e' }}>
                  {t.pwdTooShort || 'Пароль должен быть не менее 8 символов'}
                </div>
              )}

              <label style={STYLES.label}>{t.confirmPwd || 'Повторите новый пароль'}</label>
              <input
                type="password"
                value={confirm}
                onChange={e => setConfirm(stripCyrillic(e.target.value))}
                onFocus={() => setFocusedField('confirm')}
                onBlur={() => setFocusedField(null)}
                placeholder={t.confirmPwdPh || 'Введите пароль ещё раз'}
                style={{
                  ...STYLES.input,
                  ...(focusedField === 'confirm' ? STYLES.inputFocus : {}),
                }}
                autoComplete="new-password"
              />
              {confirm.length > 0 && !passwordsMatch && (
                <div style={{ ...STYLES.hint, color: '#e53e3e' }}>
                  {t.pwdMismatch || 'Пароли не совпадают'}
                </div>
              )}

              <button
                type="submit"
                disabled={!canSubmit}
                style={{
                  ...STYLES.btn,
                  ...(!canSubmit ? STYLES.btnDisabled : {}),
                }}
              >
                {loading ? (t.saving || 'Сохранение...') : (t.save || 'Сохранить')}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
